import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import {
  Snowflake,
  Wind,
  Wrench,
  ClipboardCheck,
  Building2,
  Thermometer,
  Truck,
  Users,
  ArrowRight,
} from "lucide-react";
import acServiceImage from "assets/ac-service.jpg";
import heroImage from "../assets/hero-coldroom.jpg";

// New images for the gallery
import coldroom1 from "../assets/gallery-5.jpg";
import coldroom2 from "../assets/gallery-6.jpg";
import mobileTrailer from "../assets/mobile_trailer.jpg";
import mortuaryEquip from "../assets/coldroom.jpg";

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* ✅ Hero Section */}
      <section
        className="relative text-white py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${heroImage})`,
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Expert cold chain and air conditioning solutions tailored to your business
          </p>
        </div>
      </section>

      {/* Coldroom & Mobile Solutions */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <div>
              <h2 className="text-3xl font-bold mb-6">Coldroom & Mobile Solutions</h2>
              <div className="space-y-4 text-muted-foreground mb-6">
                <p>
                  We design, manufacture, and install custom coldrooms, freezer rooms,
                  mortuary equipment, and mobile trailers for events or commercial use.
                </p>
                <p>
                  Our solutions are built to precise specifications for optimal temperature
                  control, energy efficiency, and reliability. Mobile refrigerated trailers
                  and VIP mobile toilets can be designed and hired for events nationwide.
                </p>
              </div>

              <div className="space-y-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Snowflake className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-semibold mb-2">Walk-in Coldrooms</h3>
                        <p className="text-muted-foreground">
                          Custom-built walk-in coldrooms for restaurants, supermarkets, and warehouses
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Building2 className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-semibold mb-2">Mobile & Event Trailers</h3>
                        <p className="text-muted-foreground">
                          Refrigerated and VIP trailers for sale or hire, tailored to client specifications
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Thermometer className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-semibold mb-2">Mortuary Equipment</h3>
                        <p className="text-muted-foreground">
                          Cold storage and mortuary solutions built for healthcare facilities
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div>
              <img
                src={acServiceImage}
                alt="Coldroom & AC Service"
                className="w-full h-full object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Air Conditioning Services */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Air Conditioning Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Wind className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">Installation</h3>
                  <p className="text-muted-foreground">
                    Installation of split systems, ducted air conditioning, and VRV/VRF systems for commercial and industrial applications.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Wrench className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">Repairs & Maintenance</h3>
                  <p className="text-muted-foreground">
                    Preventative maintenance, repairs, and 24/7 emergency callouts to keep your systems running efficiently.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <ClipboardCheck className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">System Design</h3>
                  <p className="text-muted-foreground">
                    Custom HVAC solutions engineered to your building’s specific requirements.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Building2 className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">Commercial Projects</h3>
                  <p className="text-muted-foreground">
                    Large-scale commercial and industrial HVAC projects from concept through commissioning.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Our Expertise</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                    <li>Custom coldroom and freezer room design & installation</li>
                    <li>Mortuary equipment & refrigerated trailers</li>
                    <li>Temperature monitoring, control, and energy-efficient systems</li>
                    <li>Mobile coldrooms and VIP trailers for events</li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                    <li>HVAC system design and installation for commercial & industrial spaces</li>
                    <li>Repairs, maintenance, and 24/7 emergency support</li>
                    <li>System upgrades, retrofits, and energy audits</li>
                    <li>Large-scale project management and commissioning</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Our Projects</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            <img
              src={coldroom1}
              alt="Walk-in Coldroom"
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
            <img
              src={coldroom2}
              alt="Freezer Room"
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
            <img
              src={mobileTrailer}
              alt="Mobile Trailer"
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
            <img
              src={mortuaryEquip}
              alt="Mortuary Equipment"
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Additional Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="pt-6 text-center">
                  <Truck className="w-10 h-10 text-primary mb-2 mx-auto" />
                  <h3 className="font-semibold mb-2">Mobile Solutions</h3>
                  <p className="text-sm text-muted-foreground">
                    Refrigerated trailers and VIP toilets for events
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <ArrowRight className="w-10 h-10 text-primary mb-2 mx-auto" />
                  <h3 className="font-semibold mb-2">System Upgrades</h3>
                  <p className="text-sm text-muted-foreground">
                    Modernize aging coldrooms and HVAC systems
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Users className="w-10 h-10 text-primary mb-2 mx-auto" />
                  <h3 className="font-semibold mb-2">Emergency Service</h3>
                  <p className="text-sm text-muted-foreground">
                    24/7 callout available for all installations
                  </p>
                </CardContent>
              </Card>
            </div>
            <Link to="/contact">
              <Button size="lg">
                Request a Quote <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
